import React from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { useCreateTemplate, useUpdateTemplate } from '@/hooks/useTemplates'
import { Template, TemplateForm as TemplateFormType } from '@/types'
import { useQuery } from '@tanstack/react-query'
import apiClient from '@/lib/api/client'
import { getApprovedSignatures as sdkGetApprovedSignatures } from '@/lib/api/generated/sdk.gen'
import { presetTemplates, extractVariables, validateVariables, PresetTemplate } from '@/data/presetTemplates'
import { BookOpen, AlertCircle, Info } from 'lucide-react'
import PresetTemplateModal from '../PresetTemplateModal'

const templateSchema = z.object({
  templateName: z.string().min(2, '模板名称至少2个字符'),
  templateContent: z.string().min(1, '请输入模板内容').max(500, '模板内容不能超过500字'),
  templateType: z.enum(['VERIFICATION', 'NOTIFICATION', 'MARKETING'], { required_error: '请选择模板类型' }),
  signatureId: z.string().optional(),
  applyReason: z.string().optional(),
})

interface TemplateFormProps {
  template?: Template
  onSuccess?: () => void
}

export default function TemplateForm({ template, onSuccess }: TemplateFormProps) {
  const isEdit = !!template
  const createTemplateMutation = useCreateTemplate()
  const updateTemplateMutation = useUpdateTemplate()
  const [presetModalOpen, setPresetModalOpen] = React.useState(false)

  // 获取已审核通过的签名列表
  const { data: signaturesData } = useQuery({
    queryKey: ['signatures', 'approved'],
    queryFn: async () => {
      const res: any = await sdkGetApprovedSignatures({ 
        client: apiClient, 
        responseStyle: 'fields', 
        throwOnError: false 
      })
      return (res as any)?.data ?? res
    },
  })

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    watch,
  } = useForm<TemplateFormType>({
    resolver: zodResolver(templateSchema),
    defaultValues: template ? {
      templateName: template.templateName,
      templateContent: template.templateContent,
      templateType: template.templateType,
      signatureId: template.signatureId?.toString(),
      applyReason: template.applyReason || '',
    } : {
      templateName: '',
      templateContent: '',
      templateType: 'VERIFICATION',
      signatureId: '',
      applyReason: '',
    },
  })

  const templateContent = watch('templateContent', '')
  const templateType = watch('templateType', '')
  const signatureId = watch('signatureId', '')

  // 获取选中的签名信息
  const selectedSignature = signaturesData?.records?.find(s => s.id === signatureId)

  // 当签名选择变化时，提供预览但不强制修改模板内容
  const getPreviewContent = () => {
    if (!signatureId || !selectedSignature) {
      return templateContent || '请输入模板内容...'
    }
    
    const signatureText = selectedSignature.fullSignatureFormat || `【${selectedSignature.signatureName}】`
    
    // 如果模板内容已经包含签名，直接返回
    if (templateContent.includes('【') && templateContent.includes('】')) {
      return templateContent
    }
    
    // 否则组合显示（仅用于预览）
    return `${signatureText}${templateContent || '模板内容'}`
  }

  // 获取纯模板内容（用于显示在输入框中）
  const getPureTemplateContent = () => {
    // 如果模板内容包含签名格式，移除签名部分
    if (templateContent.includes('【') && templateContent.includes('】')) {
      return templateContent.replace(/^【[^】]*】/, '')
    }
    return templateContent
  }

  // 当模板类型变化时，填充纯模板内容（不包含签名）
  React.useEffect(() => {
    if (templateType && (!templateContent || templateContent.trim() === '')) {
      // 使用预置模板的默认内容（纯内容，不包含签名）
      const templates = presetTemplates[templateType] || []
      if (templates.length > 0) {
        const defaultTemplate = templates[0]
        // 移除签名格式，只保留纯模板内容
        const pureContent = defaultTemplate.content.replace(/^【[^】]*】/, '')
        setValue('templateContent', pureContent)
      }
    }
  }, [templateType, templateContent, setValue])

  const onSubmit = async (data: TemplateFormType) => {
    try {
      // 确保模板内容不为空
      let finalContent = data.templateContent?.trim() || ''
      
      if (!finalContent) {
        console.error('Template content is empty')
        return
      }

      // 直接提交纯模板内容，不强制包含签名
      // 签名和模板内容在平台内部分离管理，发送时再组合
      const submitData = {
        ...data,
        templateContent: finalContent
      }

      if (isEdit) {
        await updateTemplateMutation.mutateAsync({
          id: template.id,
          data: submitData,
        })
      } else {
        await createTemplateMutation.mutateAsync(submitData)
      }
      onSuccess?.()
    } catch (error) {
      console.error('Form submission error:', error)
    }
  }

  // 提取变量
  const variables = extractVariables(templateContent)
  const variableErrors = validateVariables(variables, templateType)

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col h-full max-h-full">
        <div className="flex-1 overflow-y-auto space-y-6 pr-2 pb-4 min-h-0 max-h-full">
          <div className="space-y-2">
            <Label htmlFor="templateName">模板名称</Label>
            <Input
              id="templateName"
              {...register('templateName')}
              placeholder="请输入模板名称，不超过30个字符"
              maxLength={30}
            />
            {errors.templateName && (
              <p className="text-sm text-red-600">{errors.templateName.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label>模板类型</Label>
            <Select
              value={templateType}
              onValueChange={(value) => setValue('templateType', value as 'VERIFICATION' | 'NOTIFICATION' | 'MARKETING')}
            >
              <SelectTrigger>
                <SelectValue placeholder="请选择模板类型" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="VERIFICATION">验证码</SelectItem>
                <SelectItem value="NOTIFICATION">通知短信</SelectItem>
                <SelectItem value="MARKETING">推广短信</SelectItem>
              </SelectContent>
            </Select>
            {errors.templateType && (
              <p className="text-sm text-red-600">{errors.templateType.message}</p>
            )}
            
            {/* 验证码特殊提示 */}
            {templateType === 'VERIFICATION' && (
              <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                <div className="flex items-start">
                  <AlertCircle className="h-4 w-4 text-orange-600 mt-0.5 mr-2 flex-shrink-0" />
                  <p className="text-sm text-orange-800">
                    验证码防盗刷监控未开启，请先开启验证码防盗刷监控
                  </p>
                </div>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label>关联签名</Label>
            <Select
              value={signatureId}
              onValueChange={(value) => setValue('signatureId', value)}
              defaultValue={template?.signatureId?.toString()}
            >
              <SelectTrigger>
                <SelectValue placeholder="请选择签名" />
              </SelectTrigger>
              <SelectContent>
                {signaturesData?.records?.map((signature) => (
                  <SelectItem key={signature.id} value={signature.id.toString()}>
                    {signature.fullSignatureFormat || signature.signatureName} - {signature.signatureTypeText || signature.signatureType}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {signaturesData?.records?.length === 0 && (
              <p className="text-sm text-yellow-600">
                暂无已审核通过的签名，请先申请签名
              </p>
            )}
            <div className="flex items-center space-x-4 text-sm text-gray-600">
              <span className="cursor-pointer hover:text-blue-600">没有需要的签名，马上添加签名。</span>
              <span className="cursor-pointer hover:text-blue-600">点此查看签名列表</span>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>模板内容</Label>
              <div className="flex items-center space-x-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setPresetModalOpen(true)}
                  disabled={!templateType}
                >
                  <BookOpen className="h-4 w-4 mr-2" />
                  常用模板推荐
                </Button>
              </div>
            </div>
            
            <Textarea
              {...register('templateContent')}
              placeholder="请输入纯模板内容，使用${变量名}格式定义变量。签名将在发送时自动添加。"
              className="min-h-[120px]"
            />
            {errors.templateContent && (
              <p className="text-sm text-red-600">{errors.templateContent.message}</p>
            )}
            
            <div className="flex items-center justify-between text-sm text-gray-600">
              <span>字符数：{templateContent.length}/500</span>
              <span>预计收费：{Math.ceil(getPreviewContent().length / 70)}条短信</span>
            </div>
            
            {/* 发送预览 - 支持签名模板分离架构 */}
            {(templateContent || selectedSignature) && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="text-sm">
                  <div className="font-medium text-blue-800 mb-2">📱 发送预览（签名+模板内容）</div>
                  <div className="bg-white p-3 rounded border font-mono text-sm">
                    {getPreviewContent()}
                  </div>
                  <div className="text-xs text-blue-600 mt-2">
                    💡 平台内部分离管理签名和模板，发送时自动组合
                  </div>
                </div>
              </div>
            )}
            
            {/* 变量限制说明 */}
            <Card>
              <CardContent className="p-4">
                <div className="flex items-start">
                  <Info className="h-4 w-4 text-blue-600 mt-0.5 mr-2 flex-shrink-0" />
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium text-blue-800">变量限制</h4>
                    <ul className="text-xs text-blue-700 space-y-1">
                      <li>• 仅支持1个变量</li>
                      <li>• 4~6位仅数字、数字+字母组合或仅字母2种类型</li>
                      <li>• 变量格式：$&#123;code&#125;</li>
                      <li>• 例如：您的验证码为$&#123;code&#125;，该验证码5分钟内有效，请勿泄露于他人</li>
                    </ul>
                    <div className="cursor-pointer hover:text-blue-600 text-xs">
                      模板申请规范(含变量)
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* 变量显示 */}
            {variables.length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm">检测到的变量</Label>
                <div className="flex gap-2 flex-wrap">
                  {variables.map((variable) => (
                    <Badge key={variable} variant="outline" className="text-xs">
                      {variable}
                    </Badge>
                  ))}
                </div>
                {variableErrors.length > 0 && (
                  <div className="p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                    {variableErrors.map((error, index) => (
                      <div key={index}>• {error}</div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="applyReason">场景说明</Label>
            <Textarea
              id="applyReason"
              {...register('applyReason')}
              placeholder="详细描述您的短信模板的使用场景(例如:说明该短信在何种情境下发送,以及短信接收对象等)。如果短信中涉及到变量,也请详细说明变量的内容。"
              rows={3}
            />
            {errors.applyReason && (
              <p className="text-sm text-red-600">{errors.applyReason.message}</p>
            )}
            <div className="flex items-center justify-between text-sm text-gray-600">
              <span>字符数：{watch('applyReason')?.length || 0}/500</span>
              <span className="cursor-pointer hover:text-blue-600">提供短信完整示例</span>
            </div>
          </div>
        </div>
        
        <div className="flex justify-end space-x-2 pt-4 border-t border-gray-200 mt-6 flex-shrink-0 bg-white">
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? '提交中...' : isEdit ? '更新' : '提交申请'}
          </Button>
        </div>
      </form>

      {/* 预置模板选择Modal */}
      <PresetTemplateModal
        isOpen={presetModalOpen}
        onClose={() => setPresetModalOpen(false)}
        selectedType={templateType}
        onSelect={(template) => {
          if (selectedSignature) {
            const signatureText = selectedSignature.fullSignatureFormat || `【${selectedSignature.signatureName}】`
            const contentWithSignature = template.content.replace(/^【[^】]*】/, signatureText)
            setValue('templateContent', contentWithSignature)
          } else {
            setValue('templateContent', template.content)
          }
        }}
      />
    </>
  )
}
